import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDropzone } from 'react-dropzone';

import { ButtonPrimary, Checkbox, CustomDatePicker, Icon, ModalClose } from '../../common/components';
import { FormProvider, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import { AdminDocumentRepositoryModalWrapper } from './AdminDocumentRepositoryStyles.js';
import { DateObject } from 'react-multi-date-picker';
import moment from 'moment';
import { DATE_FORMAT, REVERSED_DATE_FORMAT_HYPHEN } from '../../common/constants';
import * as colors from '../../common/styles/colors';
import FormElement from '../../common/components/FormElement';
import { onlyLettersNumbersAndCommas } from '../utils/text/text';
import React from 'react';
import { iconsToUse } from '../../common/styles/themes';
import { getDocumentTypes } from '../../common/api/adminDocumentsApi';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';

type AdminDocumentRepositoryModalProps = {
  show: boolean;
  edit: boolean;
  item: Api.IDocumentRepositoryRequest;
  handleDownloadGenericDocument: Function;
  onSubmit: (isAccepted: boolean, item: any) => void;
  onClose: () => void;
};

const defaultProps = {
  show: true,
  edit: false,
  item: {},
  onSubmit: (isAccepted: boolean, item: any) => console.log(isAccepted),
};

type Inputs = {
  email: string;
  downloadReport: { label: string; value: string };
};

interface IOption {
  value: string;
  label: string;
}

export const AdminDocumentRepositoryModal = (props: AdminDocumentRepositoryModalProps) => {
  const { instance, accounts } = useMsal();
  const { show, onSubmit } = props;
  const [submitForm, setSubmitForm] = useState(false);

  const methods = useForm<Inputs>({ defaultValues: {} });

  const [files, setFiles] = useState<Array<File>>([]);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentType, setDocumentType] = useState<any>();
  const [startDate, setStartDate] = useState<any>('');
  const [endDate, setEndDate] = useState<any>('');
  const [audience, setAudience] = useState<any>(null);
  const [customerRefPlaceholder, setCustomerRefPlaceholder] = useState<string>('');
  const [customerRefString, setCustomerRefString] = useState<any>('');
  const [customerRefDisabled, setCustomerRefDisabled] = useState<boolean>(true);
  const [selectMessage, setSelectMessage] = useState<boolean>(false);
  const [selectPushEmail, setSelectPushEmail] = useState<boolean>(false);
  const [showUploadTooBigError, setShowUploadTooBigError] = useState<boolean>(false);
  const [showUploadMustBePdfError, setShowUploadMustBePdfError] = useState<boolean>(false);
  const [showUploadTooManyFilesError, setShowUploadTooManyFilesError] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('Draft');
  const [updateModalFileArray, setUpdateModalFileArray] = useState<Array<string>>([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<Array<IOption>>([]);
  const dropboxDisabled = updateModalFileArray.length > 0 || files.length > 0;

  const error = (error: string) => {
    const message = `API error calling: ${error}`;
    console.error(message);
    toast.error(message, { position: 'bottom-right' });
  };

  const documentTypesSuccess = (result: Common.IResult<Array<Api.IDocumentType>>) => {
    if (result && result.success) {
      const options: Array<IOption> = result.data
        .filter(res => !res.bulkActionType)
        .map(res => ({
          value: res.type,
          label: res.description,
        }));
      setDocumentTypeOptions(options);
    } else {
      error('getDocumentTypes');
    }
  };

  useEffect(() => {
    getDocumentTypes(accounts[0], instance, documentTypesSuccess, error);
  }, []);

  useEffect(() => {
    const selectedDocumentType = documentTypeOptions.find(item => item.value === props.item.type);
    setDocumentType(selectedDocumentType ? selectedDocumentType : { value: '', label: '' });
  }, [documentTypeOptions]);

  const recipientOptions = [
    {
      value: 'All',
      label: 'All',
    },
    {
      value: 'AllCustomers',
      label: 'All Customers',
    },
    {
      value: 'AllTpiUsers',
      label: 'All TPI Users',
    },
    {
      value: 'SpecificCustomers',
      label: 'Specific Customers',
    },
    {
      value: 'SpecificTpiUsers',
      label: 'Specific TPI Users',
    },
  ];

  let startDateWindowMin = moment().format(REVERSED_DATE_FORMAT_HYPHEN);
  const showPushEmail = audience?.value === 'SpecificCustomers' || audience?.value === 'SpecificTpiUsers';
  const totalSizeLimit = 20 * 1024 * 1024; // 20 MB

  const getTotalPreviousUploadedFilesSize: any = () => {
    var totalSize = 0;
    files.forEach((file: File) => {
      totalSize = totalSize + file.size;
    });
    return totalSize;
  };

  const getFileExt = (fileName: string) => {
    const fileExt = fileName.split('.').pop();

    if (fileExt === 'pdf') {
      return 'Pdf';
    } else {
      return 'Excel';
    }
  };

  const formatSize = (size: number) => {
    const fSExt = new Array('Bytes', 'KB', 'MB', 'GB');
    var i = 0;
    while (size > 900) {
      size /= 1024;
      i++;
    }
    var exactSize = Math.round(size * 100) / 100 + ' ' + fSExt[i];
    return exactSize;
  };

  const findExistingFile: any = (droppedFiles: Array<File>) => {
    var existingFiles: Array<File> = [];

    droppedFiles.forEach((file: File) => {
      const index = files.findIndex(f => f.name === file.name);
      if (index >= 0) {
        existingFiles.push(file);
      }
    });
    return existingFiles;
  };

  const onDrop = (droppedFiles: Array<File>) => {
    setShowUploadTooBigError(false);
    setShowUploadMustBePdfError(false);
    setShowUploadTooManyFilesError(false);

    const existingFilesCount = files.length;
    const droppedFilesCount = droppedFiles.length;
    if (existingFilesCount + droppedFilesCount > 1) {
      setShowUploadTooManyFilesError(true);
      return;
    }

    let alreadyUploadedTotalSize = getTotalPreviousUploadedFilesSize();
    droppedFiles.forEach((file: File) => {
      alreadyUploadedTotalSize += file.size;
    });
    if (alreadyUploadedTotalSize > totalSizeLimit) {
      setShowUploadTooBigError(true);
      return;
    }

    // Check for duplicates
    const existingFiles = findExistingFile(droppedFiles);
    if (existingFiles) {
      deleteFiles(existingFiles);
    }

    setFiles([...files, ...droppedFiles]);
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
    },
    disabled: dropboxDisabled,
  });

  const renderedFiles = files.map((file: any, index: number) => (
    <React.Fragment key={index}>
      <div className='col-12 mt-2'>
        <div className='file'>
          <div className='pl-2'>
            <div className='filepath'>{file.path}</div>
            <button className='close' onClick={event => handleDelete(event, file)}>
              <span aria-hidden='true'>
                <Icon color={colors.darkGrey} size={16} icon='cross' />
              </span>
            </button>
          </div>
          <div className='pl-2'>
            <div className='filesize'>{formatSize(file.size)}</div>
          </div>
        </div>
      </div>
    </React.Fragment>
  ));

  const renderedFilesForUpdate = updateModalFileArray.map((name: any, index: number) => (
    <React.Fragment key={index}>
      <div className='col-6 mt-2'>
        <div className='file'>
          <div className='pl-2'>
            <div className='filepath' onClick={() => handleDownloadGenericDocument(props.item.id)}>
              {name}
            </div>
            <button className='closeUpdate' onClick={() => setUpdateModalFileArray([])}>
              <span aria-hidden='true'>
                <Icon color={colors.darkGrey} size={16} icon='cross' />
              </span>
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  ));

  useEffect(() => {
    let fileNameArray: Array<string> = [];

    if (props.show && props.edit) {
      if (props.item.fileName) {
        fileNameArray.push(props.item.fileName);
        setUpdateModalFileArray(fileNameArray);
      }
    }
  }, [props.show]);

  useEffect(() => {
    const rejections: any = fileRejections;
    if (rejections) {
      rejections.map((file: any) => {
        if (file && file.errors.length > 0) {
          file.errors.map((error: any) => {
            if (error.code === 'too-many-files') {
              setShowUploadTooManyFilesError(true);
              return;
            } else if (error.code === 'file-invalid-type') {
              setShowUploadMustBePdfError(true);
              return;
            }
          });
        }
      });
    }

    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileRejections]);

  useEffect(() => {
    setDocumentTitle(props.item.title ? props.item.title : '');

    if (props.item.startDate) {
      const selectedStartDate: DateObject = new DateObject(props.item.startDate);
      setStartDate(selectedStartDate);
    } else {
      setStartDate(null);
    }

    if (props.item.endDate) {
      const selectedEndDate: DateObject = new DateObject(props.item.endDate);
      setEndDate(selectedEndDate);
    } else {
      setEndDate(null);
    }

    const selectedAudience = recipientOptions.find(
      item => item.value?.toLowerCase() === props.item.audience?.toLowerCase()
    );
    setAudience(selectedAudience);

    if (props.item.references) {
      setCustomerRefString(props.item.references.join(','));
    } else {
      setCustomerRefString('');
    }

    setSelectMessage(props.item.notifyByAlert);
    setSelectPushEmail(props.item.notifyByEmail);

    //ADD FILE HERE
    if (props.item.file) {
      setFiles([props.item.file]);
    } else {
      setFiles([]);
    }

    setStatus(props.item.status);
  }, [props.item, props.show]);

  useEffect(() => {
    var valid = false;
    valid =
      documentTitle !== '' &&
      documentType?.label !== '' &&
      startDate != null &&
      endDate != null &&
      audience != null &&
      (files.length > 0 || updateModalFileArray.length > 0) &&
      status != null;

    if (
      (audience?.value === 'SpecificCustomers' || audience?.value === 'SpecificTpiUsers') &&
      customerRefString === ''
    ) {
      valid = false;
    }

    setSubmitForm(valid);
  }, [
    documentTitle,
    documentType,
    startDate,
    endDate,
    audience,
    files,
    status,
    customerRefString,
    updateModalFileArray,
  ]);

  const handleAudienceChange = async (audience: any) => {
    setAudience(audience);
    setCustomerRefString('');
    setSelectPushEmail(false);

    if (audience.value === 'SpecificCustomers') {
      setCustomerRefDisabled(false);
      setCustomerRefPlaceholder('Input Customer account references(s) separated by a comma');
    } else if (audience.value === 'SpecificTpiUsers') {
      setCustomerRefDisabled(false);
      setCustomerRefPlaceholder('Input TPI references(s) separated by a comma');
    } else {
      setCustomerRefDisabled(true);
      setCustomerRefPlaceholder('');
    }
  };

  const handleDocumentTypeChange = async (documentType: any) => {
    setDocumentType(documentType);
  };

  const handleDownloadGenericDocument = async (id?: number) => {
    props.handleDownloadGenericDocument(id);
  };

  const handleCustomerRefChange = (event: any) => {
    if (onlyLettersNumbersAndCommas(event.target.value)) {
      setCustomerRefString(event.target.value);
    }
  };

  const handleSelectMessage = () => {
    setSelectMessage(!selectMessage);
  };

  const handleSelectPushEmail = () => {
    setSelectPushEmail(!selectPushEmail);
  };

  const deleteFiles = (filesToDelete: Array<File>) => {
    filesToDelete.forEach((file: File) => {
      const tmp = files;
      const index = tmp.findIndex(f => f.name === file.name);
      if (index >= 0) {
        tmp.splice(index, 1);
        setFiles([...tmp]);
      }
    });
  };

  const handleDelete = (event: any, file: File) => {
    event.preventDefault();
    deleteFiles([file]);
  };

  return (
    <>
      <Modal animation={false} className={'custom-modal'} show={show} size={'xl'} backdrop='static' centered>
        <AdminDocumentRepositoryModalWrapper>
          <Modal.Header>
            {props.edit ? <Modal.Title>Update Document</Modal.Title> : <Modal.Title>New Document</Modal.Title>}
            <ModalClose onClick={props.onClose} />
          </Modal.Header>

          <Modal.Body>
            <FormProvider {...methods}>
              <form>
                <div className='d-flex-column'>
                  <div className='my-2'>
                    <div className=''>Document title *</div>
                    <FormElement.Textarea
                      id='documentTitle'
                      placeholder={'Max characters 150'}
                      className='c-form__input no-resize'
                      name='documentTitle'
                      value={documentTitle}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setDocumentTitle(e.target.value);
                      }}
                      rows={1}
                      maxLength={150}
                      required={true}
                    />
                  </div>

                  <div className='container-fluid'>
                    <div className='row justify-content-between pb-4'>
                      <div className='pt-1 documentType'>
                        <div className='label'>Document type *</div>
                        <ReactSelect
                          autoFocus={false}
                          blurInputOnSelect={true}
                          placeholder={''}
                          value={documentType}
                          options={documentTypeOptions}
                          onChange={handleDocumentTypeChange}
                        />
                      </div>

                      <div className='pt-1 datePicker'>
                        <div className='label'>Start date *</div>
                        <div className=''>
                          <CustomDatePicker
                            date={startDate}
                            showIcon={true}
                            minDate={startDateWindowMin}
                            onChange={(e: any) => {
                              setStartDate(e);
                            }}
                          />
                        </div>
                      </div>

                      <div className='pt-1 datePicker'>
                        <div className='label'>End date *</div>
                        <div className=''>
                          <CustomDatePicker
                            className=''
                            date={endDate ? endDate.format(DATE_FORMAT) : ''}
                            showIcon={true}
                            minDate={startDate ? startDate : startDateWindowMin}
                            maxDate={''}
                            onChange={(e: any) => {
                              setEndDate(e);
                            }}
                            disabled={startDate === ''}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='container-fluid'>
                    <div className='row justify-content-between'>
                      <div className='pb-2'>
                        Select who can view the document or input specific customer account reference(s) or TPI
                        reference(s) if it needs to be restricted
                      </div>
                      <div className='pt-1 recipientPicker'>
                        <ReactSelect
                          autoFocus={false}
                          blurInputOnSelect={true}
                          placeholder={'Select audience'}
                          value={audience}
                          options={recipientOptions}
                          onChange={handleAudienceChange}
                        />
                      </div>

                      <div className='referenceBox'>
                        <FormElement.Textarea
                          id='specRef'
                          placeholder={customerRefPlaceholder}
                          className='c-form__input no-resize reference-box-input'
                          label={''}
                          name='specRef'
                          value={customerRefString}
                          onChange={e => handleCustomerRefChange(e)}
                          rows={1}
                          disabled={customerRefDisabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='pt-1 mb-2'>
                    <Checkbox
                      id={'select-message-grid'}
                      label={
                        'Do you want to notify the users via the message grid that this document is now available?'
                      }
                      onChange={handleSelectMessage}
                      checked={selectMessage}
                    ></Checkbox>
                  </div>
                  {showPushEmail && (
                    <div className='pt-1 mb-2'>
                      <Checkbox
                        id={'select-push-email'}
                        label={
                          'Do you want to send a push email out to the users to inform them of an important message on the portal?'
                        }
                        onChange={handleSelectPushEmail}
                        checked={selectPushEmail}
                      ></Checkbox>
                    </div>
                  )}

                  <div className='container-fluid'>
                    <div className='pb-2 pt-3'>Select file to upload</div>
                    <div className='row'>
                      <div className='pt-1 w-50'>
                        <div className=''>
                          <section className={`d-flex dropzonecontainer ${dropboxDisabled ? 'disabled' : ''}`}>
                            <div {...getRootProps({ className: 'dropzone d-flex' })}>
                              <input {...getInputProps()} />
                              <div className='loaIcon mr-5 my-auto'>
                                <Icon icon={iconsToUse.loaHealth} size={80} />
                              </div>

                              <div className='d-flex flex-column w-100 my-auto'>
                                <div className='pt-4 title'>Click to add or drag .pdf or Excel files...</div>
                                <div className='pb-4' style={{ fontStyle: 'italic' }}>
                                  maximum 1 file - 20mb total
                                </div>
                              </div>
                            </div>
                          </section>
                        </div>

                        <div className='errors'>
                          {showUploadTooBigError && <div>You can only upload files to a maximum of 20MB</div>}
                          {showUploadMustBePdfError && <div>You can only upload pdf or Excel files</div>}
                          {showUploadTooManyFilesError && <div>You can only upload a maximum of 1 file</div>}
                        </div>
                      </div>

                      <div className='w-50'>
                        <div className='filecontainer'>
                          <div>{renderedFiles}</div>
                          <div>{renderedFilesForUpdate}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='pt-2'>Status</div>
                  <FormElement.Radio
                    id='page-status'
                    className='c-form__input pt-0'
                    label={''}
                    name='page-status'
                    elements={['Ready to publish', 'Draft']}
                    value={status}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setStatus(e.target.value);
                    }}
                  />
                </div>
              </form>
            </FormProvider>
          </Modal.Body>
          <div className='float-right'>
            <ButtonPrimary
              disabled={!submitForm}
              onClick={event => {
                event.preventDefault();

                const item: Api.IDocumentRepositoryRequest = {
                  file: files[0],
                  format: files.length > 0 ? getFileExt(files[0].name) : getFileExt(updateModalFileArray[0]),
                  title: documentTitle,
                  type: documentType.value,
                  audience: audience.value,
                  notifyByEmail: selectPushEmail,
                  notifyByAlert: selectMessage,
                  startDate: startDate.format(REVERSED_DATE_FORMAT_HYPHEN),
                  endDate: endDate.format(REVERSED_DATE_FORMAT_HYPHEN),
                  status: status === 'Ready to publish' ? 'Ready' : 'Draft',
                  references: customerRefString !== '' ? customerRefString.split(',') : null,
                };

                onSubmit(submitForm, item);
              }}
              className='mt-2 pt-2 upload-btn'
              title='Save'
            ></ButtonPrimary>

            <ButtonPrimary
              onClick={event => {
                event.preventDefault();
                props.onClose();
              }}
              className='ml-4 upload-btn'
              title='Cancel'
            ></ButtonPrimary>
          </div>
        </AdminDocumentRepositoryModalWrapper>
      </Modal>
    </>
  );
};

AdminDocumentRepositoryModal.defaultProps = defaultProps;
